import React, { useState } from 'react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import Icon from '../../../components/AppIcon';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg p-3 shadow-lg text-xs">
      <p className="font-semibold text-foreground mb-2">{label}</p>
      {payload?.map((entry, i) => (
        <div key={i} className="flex items-center gap-2 text-muted-foreground">
          <span className="w-2 h-2 rounded-full" style={{ background: entry?.color }} />
          <span>{entry?.name}:</span>
          <span className="font-medium text-foreground">
            {entry?.name === 'Spent' ? `$${entry?.value?.toLocaleString()}` : entry?.value}
          </span>
        </div>
      ))}
    </div>
  );
};

const BidAnalyticsChart = ({ data, loading }) => {
  const [chartType, setChartType] = useState('area');

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-6 h-80 animate-pulse">
        <div className="h-5 bg-muted rounded w-48 mb-4" />
        <div className="h-full bg-muted/40 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-base font-semibold text-foreground">Bidding Trends</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Monthly spend and bid activity</p>
        </div>
        <div className="flex items-center gap-1 bg-muted/40 rounded-lg p-1">
          {[
            { type: 'area', icon: 'TrendingUp' },
            { type: 'bar', icon: 'BarChart2' },
            { type: 'line', icon: 'Activity' },
          ]?.map(({ type, icon }) => (
            <button
              key={type}
              onClick={() => setChartType(type)}
              className={`p-1.5 rounded-md transition-colors ${chartType === type ? 'bg-card text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <Icon name={icon} size={15} />
            </button>
          ))}
        </div>
      </div>

      <ResponsiveContainer width="100%" height={240}>
        {chartType === 'area' ? (
          <AreaChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="spentGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.3} />
                <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="bidsGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#a78bfa" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#a78bfa" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false} />
            <YAxis yAxisId="left" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Area yAxisId="left" type="monotone" dataKey="spent" name="Spent" stroke="var(--color-primary)" fill="url(#spentGrad)" strokeWidth={2} dot={{ r: 3 }} />
            <Area yAxisId="right" type="monotone" dataKey="bids" name="Bids" stroke="#a78bfa" fill="url(#bidsGrad)" strokeWidth={2} dot={{ r: 3 }} />
          </AreaChart>
        ) : chartType === 'bar' ? (
          <BarChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="spent" name="Spent" fill="var(--color-primary)" radius={[4, 4, 0, 0]} />
            <Bar dataKey="wins" name="Wins" fill="#22c55e" radius={[4, 4, 0, 0]} />
          </BarChart>
        ) : (
          <LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="bids" name="Bids" stroke="#a78bfa" strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="wins" name="Wins" stroke="#22c55e" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        )}
      </ResponsiveContainer>
    </div>
  );
};

export default BidAnalyticsChart;
